/**
 * The console's status vocabulary as DS components: the classification chip,
 * the network-state chip (with the explicit "not visible" text), and the
 * coverage meter that sits beside every ratio (dashboard design rules §5).
 *
 * Classification is a categorical identity for charts (fixed family order on
 * the `--norican-chart-*` ramp) and a cause for chips — never a severity, so no
 * family wears success or danger. The two "we do not know" families
 * (UNATTRIBUTED, UNCLASSIFIED) paint from the neutral scale so a coverage gap
 * can never be mistaken for a finding (§3.2, §5).
 */

import type { ChartConfig } from "@norican/dataviz";
import {
  CircleCheck,
  CircleEllipsis,
  Clock,
  EyeOff,
  Icon,
  Link2,
  TriangleAlert,
} from "@norican/icons";
import { Badge, Meter, Typography } from "@norican/react";

import type {
  FoConsoleCoverage,
  FoConsoleReportStatus,
  FoConsoleRumCoverage,
  FoConsoleRumJoinKind,
} from "@/lib/api";
import { heatLegend, identityConfig } from "@/lib/charts/config";
import { rumAppLabel, rumAppShortLabel } from "@/lib/rum-apps";

import {
  CLASSIFICATION_FAMILIES,
  CLASSIFICATION_LABEL,
  type ClassificationFamily,
  COVERAGE_CAPTION,
  coverageTone,
  formatCoverage,
  formatRumCoverage,
  JOIN_KIND_LABEL,
  JOIN_KIND_TONE,
  NET_STATE_LABEL,
  NET_STATE_TONE,
  parseNetState,
  PINPOINT_KIND_DETAIL,
  PINPOINT_KIND_LABEL,
  PINPOINT_VERDICT_LABEL,
  type PinpointKind,
  pinpointKind,
  pinpointVerdict,
  pinpointVerdictTone,
  presentClassification,
  REPORT_STATUS_DETAIL,
  REPORT_STATUS_LABEL,
  REPORT_STATUS_TONE,
  RUM_COVERAGE_CAPTION,
  RUM_TIER_LABEL,
  type RumTierKind,
  rumTierTone,
} from "./console-format";

/* ------------------------------------------------------------------------ *
 * Chart colours — fixed assignment, built once
 * ------------------------------------------------------------------------ */

const neutralScale = heatLegend("neutral");

/**
 * The classification chart config: the five attributable families on the
 * categorical ramp in fixed order, the unknown families on the neutral scale.
 *
 * SERVER and BROWSER joined in function v6. They keep their own identity rather
 * than folding into APPLICATION and ENDPOINT because each is backed by its own
 * measurement — what F&O said it spent, and what the browser did to the tab —
 * and a legend that hid that would undo the point of collecting them.
 */
export const CLASSIFICATION_CONFIG: ChartConfig = {
  ...identityConfig(
    ["APPLICATION", "SERVER", "NETWORK", "ENDPOINT", "BROWSER"],
    CLASSIFICATION_LABEL,
  ),
  UNATTRIBUTED: {
    label: CLASSIFICATION_LABEL.UNATTRIBUTED,
    color: neutralScale[2] ?? "var(--norican-bg-muted)",
  },
  UNCLASSIFIED: {
    label: CLASSIFICATION_LABEL.UNCLASSIFIED,
    color: neutralScale[1] ?? "var(--norican-bg-muted)",
  },
  OTHER: {
    label: CLASSIFICATION_LABEL.OTHER,
    color: neutralScale[0] ?? "var(--norican-bg-muted)",
  },
};

/** Fill for one family — the config's colour, muted for anything unknown. */
export function classificationColor(family: ClassificationFamily): string {
  return CLASSIFICATION_CONFIG[family]?.color ?? "var(--norican-bg-muted)";
}

/** The families in legend order — re-exported so panels do not re-declare it. */
export const CLASSIFICATION_ORDER = CLASSIFICATION_FAMILIES;

/**
 * The pinpoint kinds on the categorical ramp, in fixed order.
 *
 * A kind is an identity (which question the finding answers), not a severity —
 * a regression is not "worse" than a company-form finding, it is a different
 * question — so it wears the categorical ramp rather than success/warning
 * (design rules §3.2/§3.3). Fixed assignment from the full kind list, so
 * filtering the board to one company cannot repaint the survivors.
 */
export const PINPOINT_KIND_CONFIG: ChartConfig = {
  ...identityConfig(["company_form", "form_all", "regression", "aos_node"], PINPOINT_KIND_LABEL),
  other: {
    label: PINPOINT_KIND_LABEL.other,
    color: neutralScale[2] ?? "var(--norican-bg-muted)",
  },
};

/** Fill for one kind — the config's colour, muted for a kind nobody has seen. */
export function pinpointKindColor(kind: PinpointKind): string {
  return PINPOINT_KIND_CONFIG[kind]?.color ?? "var(--norican-bg-muted)";
}

/* ------------------------------------------------------------------------ *
 * Chips
 * ------------------------------------------------------------------------ */

interface ClassificationChipProps {
  /** The backend's full classification string. */
  classification: string;
  /** Show the detail after the family ("form/data (peers slow …)") beside the chip. */
  showDetail?: boolean;
  size?: "xs" | "sm" | "md";
}

/**
 * A classification as a badge with its words on it — never colour-alone. The
 * label grammar is derived, not enumerated: "APPLICATION (likely)" is the
 * application tone outlined, the three UNATTRIBUTED sub-labels each carry their
 * own text, and a string nobody has seen before renders itself on the
 * unclassified tone rather than as an empty neutral chip.
 */
export function ClassificationChip({
  classification,
  showDetail = false,
  size = "xs",
}: ClassificationChipProps) {
  const chip = presentClassification(classification);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Badge
        tone={chip.tone}
        variant={chip.variant}
        size={size}
        shape="rounded"
        title={classification}
      >
        {chip.label}
      </Badge>
      {showDetail && chip.detail && (
        <span className="text-fg-muted text-xs" title={classification}>
          {chip.detail}
        </span>
      )}
    </span>
  );
}

interface NetStateChipProps {
  /** The row's `net_state` as the wire carries it; unknown words fold to unattributed. */
  netState: string;
  size?: "xs" | "sm" | "md";
}

/**
 * The network state, in words. For an unattributed row this is the explicit
 * "network layer not visible for this user" chip — the thing that must appear
 * wherever an empty cell would otherwise read as a fine path.
 */
export function NetStateChip({ netState: rawState, size = "xs" }: NetStateChipProps) {
  const netState = parseNetState(rawState);
  const icon =
    netState === "normal" ? CircleCheck : netState === "degraded" ? TriangleAlert : EyeOff;
  return (
    <Badge
      tone={NET_STATE_TONE[netState]}
      variant={netState === "unattributed" ? "outline" : "subtle"}
      size={size}
      shape="rounded"
    >
      <span className="inline-flex items-center gap-1">
        <Icon icon={icon} size="xs" aria-hidden="true" />
        {NET_STATE_LABEL[netState]}
      </span>
    </Badge>
  );
}

interface JoinKindChipProps {
  joinKind: FoConsoleRumJoinKind;
  size?: "xs" | "sm" | "md";
}

/**
 * How the browser minute below was joined to this interaction (function v4).
 * This is the chip that decides how much the whole browser block is worth: an
 * activity-id match is *this* request's own telemetry, while the 15-minute
 * fallback may hold a neighbouring request from the same person. Never
 * colour-alone — the words and the icon both say which.
 */
export function JoinKindChip({ joinKind, size = "xs" }: JoinKindChipProps) {
  const icon = joinKind === "activity" ? Link2 : joinKind === "bucket" ? Clock : EyeOff;
  return (
    <Badge
      tone={JOIN_KIND_TONE[joinKind]}
      variant={joinKind === "activity" ? "subtle" : "outline"}
      size={size}
      shape="rounded"
    >
      <span className="inline-flex items-center gap-1">
        <Icon icon={icon} size="xs" aria-hidden="true" />
        {JOIN_KIND_LABEL[joinKind]}
      </span>
    </Badge>
  );
}

interface RumTierChipProps {
  kind: RumTierKind;
  size?: "xs" | "sm" | "md";
}

/**
 * One of the saved query's browser tiers, on its family's tone. The label is the
 * function's own string, verbatim, so the chip and the KQL cannot drift.
 *
 * Six tiers since function v5: the four v4 ones plus `throttled` (the laptop
 * clocking itself down on battery power saver) and `heap_pressure` (the tab
 * running into its JS heap ceiling). Both are ENDPOINT strings, so both wear the
 * endpoint tone without a line being added here — the tone is derived from the
 * label's family, never enumerated per tier, which is the property that lets a
 * seventh tier land in `console-format.ts` alone.
 */
export function RumTierChip({ kind, size = "xs" }: RumTierChipProps) {
  return (
    <Badge tone={rumTierTone(kind)} variant="subtle" size={size} shape="rounded">
      {RUM_TIER_LABEL[kind]}
    </Badge>
  );
}

interface ReportStatusChipProps {
  status: FoConsoleReportStatus;
  size?: "xs" | "sm" | "md";
}

/** A user report's place in the queue. `new` is the one asking for attention. */
export function ReportStatusChip({ status, size = "xs" }: ReportStatusChipProps) {
  return (
    <Badge
      tone={REPORT_STATUS_TONE[status]}
      variant={status === "closed" ? "outline" : "subtle"}
      size={size}
      shape="rounded"
      title={REPORT_STATUS_DETAIL[status]}
    >
      {REPORT_STATUS_LABEL[status]}
    </Badge>
  );
}

interface AppChipProps {
  /** The `App` value as the payload carries it — a plain string, not an enum. */
  app: string;
  /** Use the short name (charts, dense grids). */
  short?: boolean;
  size?: "xs" | "sm" | "md";
}

/**
 * Which app a row belongs to. An id the extension sent that NOVA has no label
 * for renders verbatim rather than as "unknown": it is real data, and folding
 * it away would understate the estate (§8).
 */
export function AppChip({ app, short = false, size = "xs" }: AppChipProps) {
  return (
    <Badge tone="neutral" variant="outline" size={size} shape="rounded" title={rumAppLabel(app)}>
      {short ? rumAppShortLabel(app) : rumAppLabel(app)}
    </Badge>
  );
}

interface PinpointKindChipProps {
  /** The finding's `kind` as the wire carries it; an unknown word folds to `other`. */
  kind: string;
  size?: "xs" | "sm" | "md";
}

/**
 * Which question a finding answers, in words.
 *
 * Neutral-toned on purpose: a kind is an identity, and the reserved status
 * tones belong to the verdict beside it. The `title` carries the clause that
 * qualified the finding, and a kind the backend adds later renders its own
 * string rather than an empty chip (§8 — no enum that mirrors live data).
 */
export function PinpointKindChip({ kind: rawKind, size = "xs" }: PinpointKindChipProps) {
  const kind = pinpointKind(rawKind);
  const label =
    kind === "other" ? rawKind.trim() || PINPOINT_KIND_LABEL.other : PINPOINT_KIND_LABEL[kind];
  return (
    <Badge
      tone="neutral"
      variant="outline"
      size={size}
      shape="rounded"
      title={PINPOINT_KIND_DETAIL[kind]}
    >
      {label}
    </Badge>
  );
}

interface PinpointVerdictChipProps {
  /** The finding's `verdict` as the wire carries it; an unknown word folds to `other`. */
  verdict: string;
  size?: "xs" | "sm" | "md";
}

/**
 * Which layer the evidence points at — the chip that decides whose ticket this
 * is. Tone comes from the classification family the verdict belongs to, so a
 * `path` verdict here and a `NETWORK/...` classification on the slow section
 * wear the same colour, and neither is ever colour-alone.
 */
export function PinpointVerdictChip({
  verdict: rawVerdict,
  size = "xs",
}: PinpointVerdictChipProps) {
  const verdict = pinpointVerdict(rawVerdict);
  const label =
    verdict === "other"
      ? rawVerdict.trim() || PINPOINT_VERDICT_LABEL.other
      : PINPOINT_VERDICT_LABEL[verdict];
  return (
    <Badge
      tone={pinpointVerdictTone(rawVerdict)}
      variant={verdict === "unattributed" || verdict === "other" ? "outline" : "subtle"}
      size={size}
      shape="rounded"
      title={rawVerdict}
    >
      {label}
    </Badge>
  );
}

/** A neutral placeholder chip for an unknown/unlabelled state. */
export function UnknownChip({ label }: { label: string }) {
  return (
    <Badge tone="neutral" variant="outline" size="xs" shape="rounded">
      <span className="inline-flex items-center gap-1">
        <Icon icon={CircleEllipsis} size="xs" aria-hidden="true" />
        {label}
      </span>
    </Badge>
  );
}

/* ------------------------------------------------------------------------ *
 * Coverage meter
 * ------------------------------------------------------------------------ */

export interface RatioMeterProps {
  /** The ratio, 0–100, or `null` when there is no denominator. */
  pct: number | null;
  /** The numbers beside the track — "146 of 185 (78.9%)" or the em-dash form. */
  text: string;
  /** What is being covered, e.g. "Network coverage". */
  label: string;
  /** The caveat under the track — why the number is what it is. */
  caption: string;
  size?: "sm" | "md" | "lg";
}

interface CoverageMeterProps {
  coverage: FoConsoleCoverage | null | undefined;
  label: string;
  /** Override the standard DKTAA/CZPRI caption. */
  caption?: string;
  size?: "sm" | "md" | "lg";
}

/**
 * The Cato coverage ratio as a `Meter` with its numbers and caveat. With no
 * denominator the track stays empty and the value is an em dash — never 0 %,
 * never 100 %.
 */
export function CoverageMeter({
  coverage,
  label,
  caption = COVERAGE_CAPTION,
  size = "md",
}: CoverageMeterProps) {
  return (
    <RatioMeter
      pct={coverage?.pct ?? null}
      text={formatCoverage(coverage)}
      label={label}
      caption={caption}
      size={size}
    />
  );
}

interface RumCoverageMeterProps {
  coverage: FoConsoleRumCoverage | null | undefined;
  label?: string;
  caption?: string;
  size?: "sm" | "md" | "lg";
}

/** The browser-RUM coverage ratio — browsers reporting ÷ active F&O users — same chrome. */
export function RumCoverageMeter({
  coverage,
  label = "Browsers reporting",
  caption = RUM_COVERAGE_CAPTION,
  size = "md",
}: RumCoverageMeterProps) {
  return (
    <RatioMeter
      pct={coverage?.pct ?? null}
      text={formatRumCoverage(coverage)}
      label={label}
      caption={caption}
      size={size}
    />
  );
}

/**
 * A ratio as a `Meter` with its numbers and its caveat — the chrome every
 * coverage figure on the console wears. Exported so a panel with a ratio of its
 * own (the pinpoint board's browser-telemetry coverage) renders it identically
 * instead of building a second meter. `pct: null` leaves the track empty and
 * prints whatever `text` says — "not measured", never 0 %.
 */
export function RatioMeter({ pct, text, label, caption, size = "md" }: RatioMeterProps) {
  const tone = coverageTone(pct);
  return (
    <div className="flex flex-col gap-1">
      <Meter.Root
        value={pct ?? 0}
        min={0}
        max={100}
        size={size}
        tone={tone === "neutral" ? "accent" : tone}
        aria-valuetext={`${label}: ${text}`}
      >
        <div className="mb-1 flex items-baseline justify-between gap-3">
          <Meter.Label>
            <Typography variant="caption" tone="muted">
              {label}
            </Typography>
          </Meter.Label>
          <Meter.Value>
            {() => (
              <Typography variant="body-sm" as="span" tabular className="font-semibold">
                {text}
              </Typography>
            )}
          </Meter.Value>
        </div>
        <Meter.Track>
          <Meter.Indicator />
        </Meter.Track>
      </Meter.Root>
      <Typography variant="caption" tone="muted">
        {caption}
      </Typography>
    </div>
  );
}
